/// <amd-module name="Wit/BoardsHub/Common/SwimlanesValidator" />
import { containsControlChars, containsMismatchedSurrogateChars } from "VSS/Core/Util/String";
import { IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { BoardRow } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import * as Resources from "Wit/BoardsHub/Common/Resources";

const DefaultRowId = "00000000-0000-0000-0000-000000000000";

export interface ISwimlanesValidator {
    readonly isValid: IReadonlyObservableValue<boolean>;
    validate(swimlanes: BoardRow[]): void;
    getSwimlaneNameError(swimlaneId: string): IReadonlyObservableValue<string | undefined>;
}

export class SwimlanesValidator {
    public isValid = new ObservableValue(true);

    public validate(swimlanes: BoardRow[]): void {
        for (const obs of this._nameErrorsById.values()) {
            obs.value = undefined;
        }

        const idsByName = new Map<string, string[]>();
        for (const swimlane of swimlanes) {
            if (!idsByName.has(swimlane.name)) {
                idsByName.set(swimlane.name, [swimlane.id]);
            } else {
                idsByName.get(swimlane.name)!.push(swimlane.id);
            }
        }

        for (const swimlane of swimlanes) {
            if (!this.isDefault(swimlane.id) && swimlane.name.length === 0) {
                this.setSwimlaneNameError(swimlane.id, Resources.SwimlaneNameMissingError);
            } else if (containsControlChars(swimlane.name) || containsMismatchedSurrogateChars(swimlane.name)) {
                this.setSwimlaneNameError(swimlane.id, Resources.SwimlaneNameInvalidError);
            } else if (idsByName.get(swimlane.name)!.length > 1) {
                for (const id of idsByName.get(swimlane.name)!) {
                    this.setSwimlaneNameError(id, Resources.SwimlaneNameDuplicateError);
                }
            }
        }

        for (const obs of this._nameErrorsById.values()) {
            if (obs.value) {
                this.isValid.value = false;
                return;
            }
        }

        this.isValid.value = true;
    }

    private _nameErrorsById: Map<string, IObservableValue<string | undefined>> = new Map();

    public getSwimlaneNameError(swimlaneId: string): IReadonlyObservableValue<string | undefined> {
        if (!this._nameErrorsById.has(swimlaneId)) {
            this._nameErrorsById.set(swimlaneId, new ObservableValue(undefined));
        }
        return this._nameErrorsById.get(swimlaneId)!;
    }

    private setSwimlaneNameError(swimlaneId: string, error: string): void {
        if (!this._nameErrorsById.has(swimlaneId)) {
            this._nameErrorsById.set(swimlaneId, new ObservableValue(error));
        } else {
            this._nameErrorsById.get(swimlaneId)!.value = error;
        }
    }

    private isDefault(swimlaneId: string): boolean {
        // The default swimlane has an empty guid for an id.
        return swimlaneId === DefaultRowId;
    }
}
